import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Add activity functions
  const addLoginActivity = useCallback(async (userData) => {
    try {
      const { activityService } = await import('../services/activityService');
      const userName = userData.name || userData.email || 'Unknown User';
      const loginTime = new Date().toLocaleString();
      await activityService.addActivity(
        'login',
        `${userName} logged in at ${loginTime}`,
        userData
      );
    } catch (error) {
      console.error('Error adding login activity:', error);
    }
  }, []);

  const addLogoutActivity = useCallback(async (userData) => {
    try {
      const { activityService } = await import('../services/activityService');
      const userName = userData.name || userData.email || 'Unknown User';
      const logoutTime = new Date().toLocaleString();
      await activityService.addActivity(
        'logout',
        `${userName} logged out at ${logoutTime}`,
        userData
      );
    } catch (error) {
      console.error('Error adding logout activity:', error);
    }
  }, []);

  // Get user data from Firestore
  const getUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return { id: uid, ...userDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get ID token
          const idToken = await firebaseUser.getIdToken();
          setToken(idToken);
          localStorage.setItem('token', idToken);

          // Set axios default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;

          // Get user data from Firestore
          const userData = await getUserData(firebaseUser.uid);

          const activeUser = {
            id: firebaseUser.uid,
            email: firebaseUser.email,
            name: userData?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0],
            role_id: userData?.role_id || 3,
            role_name: userData?.role_name || 'user',
            'role.id': userData?.role_id || 3,
            'role.name': userData?.role_name || 'user',
            isActive: true,
            lastLogin: new Date().toISOString(),
            ...userData
          };

          setUser(activeUser);
          localStorage.setItem('user', JSON.stringify(activeUser));
        } catch (error) {
          console.error('Error processing auth state:', error);
          setUser(null);
          setToken(null);
        }
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Refresh token periodically (Firebase tokens expire after 1 hour)
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const newToken = await currentUser.getIdToken(true);
          setToken(newToken);
          localStorage.setItem('token', newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    };

    // Refresh token every 50 minutes (before the 1-hour expiry)
    const interval = setInterval(refreshToken, 50 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Get ID token
      const idToken = await firebaseUser.getIdToken();

      // Get user data from Firestore
      const userData = await getUserData(firebaseUser.uid);

      const activeUser = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: userData?.name || firebaseUser.displayName || email.split('@')[0],
        role_id: userData?.role_id || 3,
        role_name: userData?.role_name || 'user',
        'role.id': userData?.role_id || 3,
        'role.name': userData?.role_name || 'user',
        isActive: true,
        lastLogin: new Date().toISOString(),
        ...userData
      };

      // Log login activity
      addLoginActivity(activeUser);

      return { success: true, user: activeUser, token: idToken };
    } catch (error) {
      let errorMessage = 'Login failed';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No user found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Invalid password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const { email, password, roleId = 3 } = userData;
      // Support both 'name' and 'firstName/lastName' formats
      const name = userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim();

      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update display name
      if (name) {
        await updateProfile(firebaseUser, { displayName: name });
      }

      // Get role name
      let roleName = 'user';
      if (roleId === 1) roleName = 'super-admin';
      else if (roleId === 2) roleName = 'admin';

      // Create user document in Firestore
      const now = new Date();
      const userDocData = {
        name: name || email.split('@')[0],
        email,
        role_id: roleId,
        role_name: roleName,
        created_date_time: now,
        modified_date_time: now,
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userDocData);

      // Get ID token
      const idToken = await firebaseUser.getIdToken();

      const activeUser = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: name || email.split('@')[0],
        role_id: roleId,
        role_name: roleName,
        'role.id': roleId,
        'role.name': roleName,
        isActive: true,
        lastLogin: new Date().toISOString(),
        ...userDocData
      };

      // Log registration activity
      addLoginActivity(activeUser);

      return { success: true, user: activeUser, token: idToken };
    } catch (error) {
      let errorMessage = 'Registration failed';

      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'This email is already registered';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    // Log logout activity before signing out
    if (user) {
      addLogoutActivity(user);
    }

    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }

    // Clear local state
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }, [user, addLogoutActivity]);

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      let errorMessage = 'Failed to send reset email';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No user found with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    resetPassword,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
